import { Suspense, lazy } from 'react';
import { Outlet, createBrowserRouter, replace } from 'react-router-dom';

import Spinner from '~/common/components/utils/Spinner';
import RootBoundary from '~/common/errors/RootBoundary.tsx';
import AppLayout from '~/common/layouts/AppLayout.tsx';
import EmptyLayout from '~/common/layouts/EmptyLayout.tsx';
import { getCurrentUser } from '~/features/auth/api/auth.api.ts';
import AuthGuard from '~/features/auth/components/AuthGuard.tsx';
import RoleGuard from '~/features/auth/components/RoleGuard.tsx';
import { buildSignInPathWithReturnTo } from '~/features/auth/utils/auth.util.ts';

import type { ReactNode } from 'react';
import type { LoaderFunctionArgs, RouteObject } from 'react-router-dom';

/* eslint-disable @typescript-eslint/naming-convention */
const SignIn = lazy(() => import('~/pages/auth/sign-in'));
const SignUp = lazy(() => import('~/pages/auth/sign-up'));
const ForgotPassword = lazy(() => import('~/pages/auth/forgot-password'));
const ResetPassword = lazy(() => import('~/pages/auth/reset-password'));
const Home = lazy(() => import('~/pages'));
const ClassDetail = lazy(() => import('~/pages/class/[id]'));
const TutorDetail = lazy(() => import('~/pages/tutor/[id]'));
const Search = lazy(() => import('~/pages/search'));
const ClassRequest = lazy(() => import('~/pages/request/[classId]'));
const ChatList = lazy(() => import('~/pages/chat'));
const ChatRoom = lazy(() => import('~/pages/chat/[roomId]'));
const ReviewWrite = lazy(() => import('~/pages/review/[requestId]'));
const Onboarding = lazy(() => import('~/pages/onboarding'));
const TutorConsole = lazy(() => import('~/pages/tutor-console'));
const TutorRequestDetail = lazy(() => import('~/pages/tutor-console/[requestId]'));
const TutorClasses = lazy(() => import('~/pages/tutor-console/classes'));
const TutorClassEdit = lazy(() => import('~/pages/tutor-console/classes/[classDbId]'));
const TutorSettlement = lazy(() => import('~/pages/tutor-console/settlement'));
const AdminHome = lazy(() => import('~/pages/admin'));
const AdminOrders = lazy(() => import('~/pages/admin/orders'));
const AdminTutorDetail = lazy(() => import('~/pages/admin/tutors/[tutorId]'));
const Checkout = lazy(() => import('~/pages/checkout/[id]'));
const PaymentResult = lazy(() => import('~/pages/payment/result'));
const MyPage = lazy(() => import('~/pages/mypage'));
const OrderDetail = lazy(() => import('~/pages/mypage/orders/[id]'));
const Refund = lazy(() => import('~/pages/mypage/refund/[id]'));
const Settings = lazy(() => import('~/pages/settings'));
const ProfileEdit = lazy(() => import('~/pages/settings/profile'));
const Legal = lazy(() => import('~/pages/legal/[doc]'));
const Support = lazy(() => import('~/pages/support'));
const NotFound = lazy(() => import('~/pages/_not-found'));
/* eslint-enable @typescript-eslint/naming-convention */

/**
 * lazy 페이지 라우트 1건 — 경로와 **경로로 키를 준 Suspense 경계**를 한 번에 만든다.
 *
 * @description 최상위(App.tsx)에 경계를 1개만 두면 react-router 의 transition 내비게이션이
 *  **이미 마운트된 트리를 fallback 으로 교체하지 않아** lazy 청크 로딩 구간 내내
 *  "URL 은 새 페이지, 화면은 옛 페이지"가 된다(청크 3s 지연 주입 시 3.4s 잔존·스피너 0).
 *  라우트마다 **새로 마운트되는** 경계를 두면 그 구간에 표시할 기존 콘텐츠가 없으므로
 *  fallback 이 즉시 노출된다.
 *  `key`(라우트 경로) 가 필수다 — 형제 라우트끼리는 트리 상 같은 위치라 key 없이는 React 가
 *  경계를 *갱신*으로 취급해(= 기존 콘텐츠 보유) 잔존 문제가 그대로 재현된다(실측 확인).
 *  **`path` 를 그대로 key 로 쓰는 게 이 함수의 존재 이유다** — 경로를 두 번 적게 하면 복붙 오타로
 *  key 만 다른 라우트와 겹쳐 경계 리셋이 조용히 깨진다(위 잔존 버그 재발).
 */
function lazyRoute(path: string, element: ReactNode): RouteObject {
  return {
    path,
    element: (
      <Suspense key={path} fallback={<Spinner fullScreen />}>
        {element}
      </Suspense>
    ),
  };
}

/**
 * 보호 라우트 진입 게이트 — 토큰 없으면 라우트 렌더 전에 로그인으로 축출(returnTo 보존).
 *
 * @description 축출을 컴포넌트 렌더 결과(`<Navigate>`)로 처리하면 **POP(뒤로가기) 전환에서
 *  삼켜진다** — 로그아웃 직후 뒤로가기로 보호 라우트에 돌아오면 리다이렉트가 유실되고
 *  화면이 빈 채 고착된다(재현 100%). loader 는 전환 라이프사이클 *내부*에서 판정돼
 *  보호 트리 **바깥에서 들어오는** 모든 진입(PUSH·POP·새로고침·직접 URL)에 동일하게 적용된다.
 *  형제 보호 라우트끼리의 이동(`/settings` ↔ `/mypage`)은 pathless 부모가 계속 매치돼
 *  기본 revalidation 조건을 만족하지 못하므로 `shouldRevalidate: () => true` 로 재실행을 강제한다
 *  (없으면 그 구간만 `<Navigate>` 로 되돌아가 H6 이 좁게 재현된다 — 트리거는 다른 탭 로그아웃 등
 *  외생적 토큰 소실).
 *  `replace` 로 축출해야 뒤로가기가 다시 보호 라우트로 돌아오는 루프를 만들지 않는다.
 */
async function protectedRouteLoader({ request }: LoaderFunctionArgs) {
  // Supabase 세션 조회는 비동기다. loader 가 await 하므로 판정 전에 화면이 그려지지 않는다
  // (동기 쿠키 검사 시절과 동일한 보장을 유지한다).
  if (await getCurrentUser()) return null;

  const { pathname, search } = new URL(request.url);
  return replace(buildSignInPathWithReturnTo(pathname, search));
}

const router = createBrowserRouter([
  // 인증 불필요 — 로그인/회원가입
  // (로그인 상태 진입도 허용: 가족 계정 추가 등 정당한 시나리오. 가입 성공 시 직전 세션 정리는 회원가입 핸들러 소관)
  {
    element: <EmptyLayout />,
    errorElement: <RootBoundary />,
    children: [
      lazyRoute('/auth/sign-in', <SignIn />),
      lazyRoute('/auth/sign-up', <SignUp />),
      lazyRoute('/auth/forgot-password', <ForgotPassword />),
      lazyRoute('/auth/reset-password', <ResetPassword />),
    ],
  },
  // 소비자 앱 셸 (폭에 따라 하단 탭 ↔ 상단 전역 네비게이션) — 로그인 없이 둘러보기 가능
  {
    element: <AppLayout />,
    errorElement: <RootBoundary />,
    children: [
      // 공개 — 카탈로그 둘러보기·정책·고객센터
      lazyRoute('/', <Home />),
      lazyRoute('/class/:id', <ClassDetail />),
      lazyRoute('/tutor/:id', <TutorDetail />),
      lazyRoute('/search', <Search />),
      lazyRoute('/legal/:doc', <Legal />),
      lazyRoute('/support', <Support />),
      // 인증 필요 — 결제 시도·마이페이지(토큰 없으면 로그인 유도 + returnTo 복귀)
      {
        loader: protectedRouteLoader,
        shouldRevalidate: () => true,
        element: (
          <AuthGuard>
            <Outlet />
          </AuthGuard>
        ),
        children: [
          // 매칭 — 신청·채팅은 본인 데이터라 토큰 없이는 진입시키지 않는다.
          lazyRoute('/onboarding', <Onboarding />),
          lazyRoute('/request/:classId', <ClassRequest />),
          lazyRoute('/chat', <ChatList />),
          lazyRoute('/chat/:roomId', <ChatRoom />),
          lazyRoute('/review/:requestId', <ReviewWrite />),
          // 지도사 뷰 — 신청 접수·수락. 지도사로 가입한 계정만 진입한다(실제 차단은 RLS).
          lazyRoute(
            '/tutor-console',
            <RoleGuard allow="tutor">
              <TutorConsole />
            </RoleGuard>,
          ),
          /*
            클래스 관리가 `:requestId` 보다 **앞에** 와야 한다 — 뒤에 두면 `/tutor-console/classes` 가
            requestId='classes' 로 잡혀 "존재하지 않는 신청" 화면이 뜬다.
          */
          lazyRoute(
            '/tutor-console/classes',
            <RoleGuard allow="tutor">
              <TutorClasses />
            </RoleGuard>,
          ),
          lazyRoute(
            '/tutor-console/settlement',
            <RoleGuard allow="tutor">
              <TutorSettlement />
            </RoleGuard>,
          ),
          lazyRoute(
            '/tutor-console/classes/:classDbId',
            <RoleGuard allow="tutor">
              <TutorClassEdit />
            </RoleGuard>,
          ),
          lazyRoute(
            '/tutor-console/:requestId',
            <RoleGuard allow="tutor">
              <TutorRequestDetail />
            </RoleGuard>,
          ),
          lazyRoute('/checkout/:id', <Checkout />),
          lazyRoute('/payment/result', <PaymentResult />),
          lazyRoute('/mypage', <MyPage />),
          lazyRoute('/mypage/orders/:id', <OrderDetail />),
          lazyRoute('/mypage/refund/:id', <Refund />),
          lazyRoute('/settings', <Settings />),
          lazyRoute('/settings/profile', <ProfileEdit />),
          // 운영 콘솔 — 관리자만. 화면 가드는 안내이고 실제 차단은 RLS(private.is_admin)다.
          lazyRoute(
            '/admin',
            <RoleGuard allow="admin">
              <AdminHome />
            </RoleGuard>,
          ),
          lazyRoute(
            '/admin/orders',
            <RoleGuard allow="admin">
              <AdminOrders />
            </RoleGuard>,
          ),
          lazyRoute(
            '/admin/tutors/:tutorId',
            <RoleGuard allow="admin">
              <AdminTutorDetail />
            </RoleGuard>,
          ),
        ],
      },
      lazyRoute('*', <NotFound />),
    ],
  },
]);

export default router;
